<template>
  <div class="docs-layout">
    <aside class="docs-nav">
      <div class="docs-nav-label">Documentation</div>
      <p class="docs-nav-hint">
        Operator guides from the repo <code>docs/</code> folder.
      </p>
      <router-link
        v-for="doc in docs"
        :key="doc.slug"
        :to="`/docs/${doc.slug}`"
        class="docs-nav-item"
        :class="{ 'is-active': activeSlug === doc.slug }"
      >
        <span class="docs-nav-title">{{ doc.title }}</span>
        <span class="docs-nav-summary">{{ doc.summary }}</span>
      </router-link>
    </aside>

    <article class="docs-panel">
      <div v-if="!activeDoc" class="docs-empty">
        <div class="docs-empty-title">Choose a document</div>
        <p class="tw-text-muted tw-text-sm">
          Hosting, auth, MLS vendor/feeds, demo walkthrough, and MLS Grid sync notes.
        </p>
        <ul class="docs-index">
          <li v-for="doc in docs" :key="doc.slug">
            <router-link :to="`/docs/${doc.slug}`">{{ doc.title }}</router-link>
            <span class="tw-text-muted"> — {{ doc.summary }}</span>
          </li>
        </ul>
      </div>
      <template v-else>
        <div class="docs-meta">
          <span class="docs-file">{{ activeDoc.file }}</span>
          <a
            v-if="externalAdmin"
            :href="externalAdmin"
            target="_blank"
            rel="noopener noreferrer"
            class="docs-external"
          >
            MLS Grid admin
            <i class="pi pi-external-link" />
          </a>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="docs-body" v-html="html" @click="onClick" />
      </template>
    </article>
  </div>
</template>

<script>
import { marked } from 'marked'
import { DOCS, getDocBySlug, MD_FILE_TO_SLUG } from '../lib/docsCatalog'

marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: true,
  mangle: false,
})

function rewriteInternalLinks(html) {
  return html.replace(
    /href="(\.\/)?([A-Za-z0-9_.-]+\.md)(#[^"]*)?"/g,
    (match, _dot, file, hash) => {
      const slug = MD_FILE_TO_SLUG[file]
      if (!slug) return match
      return `href="/docs/${slug}${hash || ''}" data-internal="1"`
    }
  )
}

function enhanceExternalLinks(html) {
  return html.replace(
    /<a href="(https?:\/\/[^"]+)"/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer"'
  )
}

export default {
  data() {
    return { docs: DOCS }
  },
  computed: {
    activeSlug() {
      return this.$route.params.slug || null
    },
    activeDoc() {
      return this.activeSlug ? getDocBySlug(this.activeSlug) : null
    },
    html() {
      if (!this.activeDoc) return ''
      let out = marked.parse(this.activeDoc.markdown || '')
      out = rewriteInternalLinks(out)
      out = enhanceExternalLinks(out)
      return out
    },
    externalAdmin() {
      return this.activeSlug === 'mls-vendor' || this.activeSlug === 'mls-grid'
        ? 'https://app.mlsgrid.com/admin'
        : null
    },
  },
  watch: {
    activeSlug: {
      immediate: true,
      handler(slug) {
        if (slug && !getDocBySlug(slug)) {
          this.$router.replace('/docs')
        }
      },
    },
  },
  methods: {
    onClick(e) {
      const a = e.target.closest('a')
      if (!a) return
      const href = a.getAttribute('href') || ''
      if (a.dataset.internal === '1' || href.startsWith('/docs/')) {
        e.preventDefault()
        this.$router.push(href)
      }
    },
  },
}
</script>

<style scoped>
.docs-layout {
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.docs-nav {
  position: sticky;
  top: 0.5rem;
  padding: 0.85rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.docs-nav-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.docs-nav-hint {
  margin: 0.4rem 0 0.75rem;
  font-size: 0.75rem;
  color: #a1a1aa;
  line-height: 1.4;
}

.docs-nav-item {
  display: block;
  padding: 0.65rem 0.7rem;
  margin-bottom: 0.35rem;
  border-radius: 6px;
  text-decoration: none;
  color: inherit;
  border: 1px solid transparent;
}

.docs-nav-item:hover {
  background: #fafafa;
  border-color: #e4e4e7;
}

.docs-nav-item.is-active {
  background: #18181b;
  color: #fff;
  border-color: #18181b;
}

.docs-nav-item.is-active .docs-nav-summary {
  color: #a1a1aa;
}

.docs-nav-title {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
}

.docs-nav-summary {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #71717a;
  line-height: 1.35;
}

.docs-panel {
  padding: 1.25rem 1.4rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-height: 20rem;
}

.docs-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f4f4f5;
}

.docs-file {
  font-size: 0.75rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #71717a;
}

.docs-external {
  font-size: 0.8rem;
  font-weight: 600;
  color: #18181b;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.docs-external:hover {
  text-decoration: underline;
}

.docs-empty-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #18181b;
}

.docs-index {
  margin: 1rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.875rem;
  line-height: 1.6;
}

.docs-index a {
  font-weight: 600;
  color: #18181b;
}

.docs-body {
  font-size: 0.925rem;
  line-height: 1.55;
  color: #27272a;
}

.docs-body ::v-deep h1 {
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 0.85rem;
  color: #18181b;
}

.docs-body ::v-deep h2 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 1.6rem 0 0.55rem;
  color: #18181b;
}

.docs-body ::v-deep h3 {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 1.25rem 0 0.4rem;
}

.docs-body ::v-deep p,
.docs-body ::v-deep ul,
.docs-body ::v-deep ol {
  margin: 0.55rem 0;
}

.docs-body ::v-deep li {
  margin: 0.25rem 0;
}

.docs-body ::v-deep a {
  color: #18181b;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.docs-body ::v-deep code {
  font-size: 0.82em;
  padding: 0.1em 0.35em;
  border-radius: 4px;
  background: #f4f4f5;
  border: 1px solid #e4e4e7;
}

.docs-body ::v-deep pre {
  margin: 0.85rem 0;
  padding: 0.85rem 1rem;
  overflow-x: auto;
  border-radius: 8px;
  background: #18181b;
  color: #fafafa;
  font-size: 0.8rem;
  line-height: 1.45;
}

.docs-body ::v-deep pre code {
  background: transparent;
  border: 0;
  padding: 0;
  color: inherit;
}

.docs-body ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.85rem 0;
  font-size: 0.85rem;
}

.docs-body ::v-deep th,
.docs-body ::v-deep td {
  border: 1px solid #e4e4e7;
  padding: 0.5rem 0.65rem;
  text-align: left;
  vertical-align: top;
}

.docs-body ::v-deep th {
  background: #fafafa;
  font-weight: 700;
}

.docs-body ::v-deep hr {
  border: 0;
  border-top: 1px solid #e4e4e7;
  margin: 1.5rem 0;
}

.docs-body ::v-deep blockquote {
  margin: 0.85rem 0;
  padding: 0.5rem 0.85rem;
  border-left: 3px solid #18181b;
  background: #fafafa;
  color: #52525b;
}

@media (max-width: 900px) {
  .docs-layout {
    grid-template-columns: 1fr;
  }

  .docs-nav {
    position: static;
  }
}
</style>
